import React, { useContext } from 'react';
import { useMemo } from 'react';
import { Redirect, Route } from 'react-router-dom';

import { get, isEmpty } from 'lodash';
import moment from 'moment';

import { MainContext } from '@ezbobdev/ui-framework';
import { Tools } from '@ezbobdev/ui-sdk';

/**
 * Secured route checking if the user is logged in
 */
const AuthenticatedRoute = (props) => {
  const { location, path } = props;
  const { actions } = useContext(MainContext);

  if (!path) {
    console.warn('Path is missing in AuthenticatedRoute');
    return null;
  }

  const isLoggedIn = actions.userState.isLoggedIn();

  if (!isLoggedIn) {
    return (
      <Redirect to={{ pathname: actions.settings.getPageRoute('customerLogin').url, state: { referrer: location } }} />
    );
  }

  return <Route {...props} />;
};

/**
 * Secured route checking if the user is logged and has permissions to view page
 */
const AuthorizedRoute = (props) => {
  const { name, location, path } = props;

  const { actions, sdkActions } = useContext(MainContext);

  if (!path) {
    console.warn('Path is missing in AuthorizedRoute');
    return null;
  }

  const product = Tools.getOption('product');

  const isLoggedIn = actions.userState.isLoggedIn();
  const { scopes } = actions.settings.getPageRoute(name) ?? {};
  const { data } = sdkActions.handlers.getResponses('users.getUserPermissions') ?? {};

  const tenantScopes = get(data, 'tenantData', {});
  const productScopes = get(data, product, {});

  const userHasAccess = useMemo(() => {
    if (isEmpty(data)) {
      console.error('No user data found from API');
      return false;
    }

    if (isEmpty(productScopes) && isEmpty(tenantScopes)) {
      console.error('No product or tenant permissions found from API');
      return false;
    }

    if (!scopes) {
      console.error('Missing UI route configuration scopes');
      return false;
    }
    const relevantScopes = { ...productScopes, ...tenantScopes };
    return scopes.some((scope) => Object.keys(relevantScopes)?.includes(scope));
  }, [scopes, data]);

  if (!isLoggedIn) {
    return (
      <Redirect to={{ pathname: actions.settings.getPageRoute('customerLogin').url, state: { referrer: location } }} />
    );
  }

  if (!userHasAccess) {
    console.error('Unauthorized');
    return null;
  }

  return <Route {...props} />;
};

const RedirectRoute = (props) => {
  return <Redirect {...props} />;
};

const AppRoute = ({ component: Component, location, path, ...rest }) => {
  const { actions, sdkActions } = useContext(MainContext);

  if (!path) {
    console.warn('Path is missing in AppRoute');
    return null;
  }

  const maintenanceData = sdkActions.handlers.getResponses('system.getMaintenanceData')?.data ?? [];

  const activeProductMaintenance = maintenanceData.find(
    ({ maintenanceType, startDate }) => maintenanceType === 'PRODUCT' && startDate,
  );

  const isInMaintenance =
    activeProductMaintenance &&
    moment().isBetween(moment(activeProductMaintenance.startDate), moment(activeProductMaintenance.endDate));

  const maintenanceRoute = actions.settings.getPageRoute('systemMaintenance').url;
  return (
    <Route
      {...rest}
      path={path}
      render={(props) => {
        if (isInMaintenance && location.pathname !== maintenanceRoute) {
          return <Redirect to={maintenanceRoute} />;
        }

        return <Component {...props} />;
      }}
    />
  );
};

AuthenticatedRoute.displayName = 'AuthenticatedRoute';
AuthorizedRoute.displayName = 'AuthorizedRoute';
RedirectRoute.displayName = 'RedirectRoute';
AppRoute.displayName = 'AppRoute';

export { AuthenticatedRoute, AuthorizedRoute, RedirectRoute, AppRoute };

export default Route;
